import { Badge } from '@chakra-ui/react';
import { groupBy, round, uniqBy } from 'lodash';
import { ReactElement, useCallback, useMemo } from 'react';
import { useFlag } from '@unleash/proxy-client-react';
import { useTranslation } from 'react-i18next';
import { DiffText } from '../project/project-history/DiffText';
import { Property, PropertyType } from '../../domain/property-types';
import { formatDragData } from '../../utils/property-dragging-utils';
import {
  convertQuantityToSIUnits,
  convertSIQuantity,
  formatUnit,
} from '../../utils/unit-utils';
import { getCategoriesWithFriendlyName } from '../orderContractor/CategoriesMenuListCheckBoxes';
import StyledKeyValuesAccordion, {
  TruncatedValue,
} from './StyledKeyValuesProperties';
import { EventType, ProjectSettingsType } from 'src/gql/graphql';

type BodyProps = {
  propertiesForCategory: Property[];
  projectSettings?: ProjectSettingsType;
  diffMap?: Record<string, { old?: unknown; new?: unknown }>;
};
const getDragData = (category: string) => (key: string) =>
  formatDragData({
    category,
    propertyName: key,
    displayName: key,
  });

const BooleanValue = ({ value }: { value: boolean }) =>
  value ? (
    <Badge colorScheme={'blue'} size={'md'} px={2}>
      Yes
    </Badge>
  ) : (
    <Badge colorScheme={'purple'} size={'md'} px={2}>
      No
    </Badge>
  );

export const PropertiesAccordionBody = ({
  propertiesForCategory,
  projectSettings,
  diffMap,
}: BodyProps): React.ReactElement => {
  const propertiesByAttribute: Record<string, Property[]> = useMemo(
    () => groupBy(propertiesForCategory, (property) => property.attribute.name),
    [propertiesForCategory]
  );

  const { t: translate } = useTranslation('categories');

  const enableUnitSettings = useFlag('enable-unit-settings');

  const getQuantityFromSettings = useCallback(
    (value: number, units: string) => {
      if (projectSettings?.unitSettings) {
        const resultQuantity = convertSIQuantity(
          { value, units },
          projectSettings.unitSettings
        );

        return {
          value: resultQuantity.value as number,
          units: resultQuantity.units,
        };
      }

      return { value, units };
    },
    [projectSettings]
  );

  const propertyNames = Object.keys(propertiesByAttribute).sort();

  return (
    <>
      {/* eslint-disable-next-line complexity */}
      {propertyNames.map((propertyName) => {
        const properties = propertiesByAttribute[propertyName];
        const uniqueProperties = uniqBy(
          properties,
          (property) => `${property.type}-${property.value}-${property.units}`
        );
        const attributeName = uniqueProperties[0].attribute.name;
        const attributeCategory = uniqueProperties[0].attribute.category;

        let elementValue: ReactElement;
        let eventType: EventType | undefined;
        const booleanProperties = uniqueProperties.filter(
          (property) => property.type === PropertyType.Boolean
        );
        if (booleanProperties.length > 0) {
          elementValue = (
            <>
              {booleanProperties.map((property) => {
                const { value } = property;
                return <BooleanValue key={value} value={value === 1} />;
              })}
            </>
          );
        } else {
          const display = uniqueProperties
            .map((property) => {
              const { value: propertyValue, units: propertyUnits } = property;
              if (
                attributeName === 'Category' &&
                typeof propertyValue === 'string'
              ) {
                const friendlyCategoryValue =
                  getCategoriesWithFriendlyName(translate).find((category) =>
                    category.modernValues.includes(propertyValue)
                  )?.friendlyName || propertyValue;

                return friendlyCategoryValue;
              }
              let { value: resultValue, units: resultUnits } =
                convertQuantityToSIUnits({
                  value: propertyValue,
                  units: formatUnit(propertyUnits),
                });

              if (
                enableUnitSettings &&
                typeof resultValue === 'number' &&
                resultUnits
              ) {
                const result = getQuantityFromSettings(
                  resultValue as number,
                  resultUnits
                );
                resultValue = result.value;
                resultUnits = result.units;
              }

              const formattedValue =
                typeof resultValue === 'number'
                  ? round(resultValue, 2)
                  : resultValue;
              return resultUnits
                ? `${formattedValue} ${resultUnits}`
                : formattedValue;
            })
            .join(', ');
          const diff = diffMap?.[attributeName];
          if (diff && diff.old === undefined && diff.new !== undefined) {
            eventType = EventType.Created;
            elementValue = (
              <TruncatedValue>{String(diff.new ?? display)}</TruncatedValue>
            );
          } else if (
            diff &&
            (diff.old !== undefined || diff.new !== undefined) &&
            diff.old !== diff.new
          ) {
            eventType = EventType.Modified;
            elementValue = (
              <DiffText
                oldValue={String(diff.old ?? display)}
                newValue={String(diff.new ?? display)}
              />
            );
          } else {
            elementValue = <TruncatedValue>{display}</TruncatedValue>;
          }
        }

        return (
          <StyledKeyValuesAccordion
            key={attributeName}
            getDragData={getDragData(attributeCategory)}
            keyValues={[
              {
                key: attributeName,
                value: elementValue,
                eventType,
              },
            ]}
          />
        );
      })}
    </>
  );
};

export default PropertiesAccordionBody;
